import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import type { GetSearchResponse } from '@responses/search';
import { infiniteQueryOptions, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';

export const fetchSearch = async (params?: {
  size?: number;
  search?: string;
  type?: string;
  sort?: 'relevance' | 'recent';
  cursor?: string;
  pitId?: string;
}): Promise<GetSearchResponse> => {
  const { size, search, type, sort, cursor, pitId } = params ?? {};

  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/search',
    params: {
      size,
      search,
      type,
      sort,
      cursor,
      pitId,
    },
  });

  const response = await axios<GetSearchResponse>(networkPayload);
  return response.data;
};

export const searchQueries = {
  listSearch: ({
    search,
    type,
    sort,
    size,
    enabled,
  }: {
    search?: string;
    type?: string;
    sort?: 'relevance' | 'recent';
    size?: number;
    enabled?: boolean;
  }) =>
    infiniteQueryOptions({
      queryKey: ['search', search, type, sort, size],
      queryFn: async ({ pageParam }) => {
        const data = await fetchSearch({
          cursor: pageParam?.nextCursor,
          pitId: pageParam?.pitId,
          search,
          type,
          sort,
          size,
        });
        return { ...data, pitId: data.pitId ?? '', nextCursor: data.nextCursor ?? '' };
      },
      getNextPageParam: (lastPage) => {
        return lastPage.nextCursor ? { pitId: lastPage.pitId, nextCursor: lastPage.nextCursor } : undefined;
      },
      initialPageParam: { pitId: '', nextCursor: '' },
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
      enabled,
    }),
};

export function useSearch({
  search,
  type,
  sort,
  size,
  enabled,
}: {
  search?: string;
  type?: string;
  sort?: 'relevance' | 'recent';
  size?: number;
  enabled?: boolean;
}) {
  return useInfiniteQuery(searchQueries.listSearch({ search, type, sort, size, enabled }));
}
